import React from 'react';
import _ from 'underscore';

import PrimerRowHeader from 'analyses/primers/PrimerRowHeader';
import PrimerRowInterval from 'analyses/primers/PrimerRowInterval';
import PrimerRowIntervalList from 'analyses/primers/PrimerRowIntervalList';
import PrimerRowLongField from 'analyses/primers/PrimerRowLongField';
import PrimerRowOnlyFields from 'analyses/primers/PrimerRowOnlyFields';
import {Task} from 'analyses/primers/PrimerWizardConstants';
import PRIMER_WIZARD_PARAMS, {PrimerWizardParams} from 'analyses/primers/PrimerWizardParams';
import PrimerWizardSection from 'analyses/primers/PrimerWizardSection';
import * as analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import {InputChangeEvent, Optional} from 'util/Types';
import {MELTING_TEMP_ABBREVIATION} from 'util/UnicodeSymbols';

const {PCR, QPCR_DYES, QPCR_PROBE, SEQUENCING} = Task;

interface PrimerWizardSelection {
  start: number;
  end: Optional<number>;
}

interface Inputs {
  value: string;
  onChange: (e?: {target: {value: string}}) => void;
  placeholder: string | number | null;
}

export interface PrimerWizardProps {
  parameters: {task: Task};
  updateParameters: (params: {task: Task}) => void;
  showTmParameters: () => void;
  selection: PrimerWizardSelection;
  getDefault: (param: PrimerWizardParams, task: Task) => string | number | null;
}

class PrimerWizard extends React.Component<PrimerWizardProps> {
  setFromSelection(
    startParam: PrimerWizardParams,
    endParam: PrimerWizardParams,
    start: string,
    end: string
  ): void {
    const startLabel = startParam.label;
    const endLabel = endParam.label;
    // eslint-disable-next-line no-restricted-syntax
    const newParams = _.extend({}, this.props.parameters);
    newParams[startLabel] = start;
    newParams[endLabel] = end;
    if (!start.length) {
      delete newParams[startLabel];
    }
    if (!end.length) {
      delete newParams[endLabel];
    }
    this.props.updateParameters(newParams);
  }

  resetParameters = (): void => {
    const newParams = {task: this.props.parameters.task};
    this.props.updateParameters(newParams);
  };

  private handleChangeParamValue(name: string, val?: string): void {
    if (name === 'task') {
      analytics.track('primer', 'change_task', {
        product: ANALYTICS_MODULE.MOLBIO,
        task: val,
      });
    }
    // TODO: refactor _.extend to be type-explicit
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const newParams = _.clone(this.props.parameters) as any;
    newParams[name] = val;
    if (!val!.length) {
      delete newParams[name];
    }
    this.props.updateParameters(newParams);
  }

  renderHeader(): JSX.Element {
    return (
      <FormGroup>
        <Label md={2}>Task</Label>
        <Col md={6}>
          <select
            value={this.props.parameters.task ?? ''}
            onChange={(e) => {
              this.handleChangeParamValue('task', e.target.value);
            }}
          >
            <option value={PCR}>PCR</option>
            <option value={QPCR_PROBE}>qPCR (Hybridization Probe)</option>
            <option value={QPCR_DYES}>qPCR (Intercalating Dyes)</option>
            <option value={SEQUENCING}>Sequencing</option>
          </select>
        </Col>
        <Col md={2}>
          <a className="u-input-line-height" onClick={this.props.showTmParameters}>
            {MELTING_TEMP_ABBREVIATION} params
          </a>
        </Col>
        <Col md={2}>
          <a className="u-input-line-height" onClick={this.resetParameters}>
            Reset
          </a>
        </Col>
      </FormGroup>
    );
  }

  getInputs(params: Array<PrimerWizardParams>): Array<Inputs> {
    return params.map((param) => ({
      // @ts-expect-error
      value: this.props.parameters[param.label] ?? '',
      onChange: (e: InputChangeEvent) => {
        this.handleChangeParamValue(param.label, e.target.value);
      },
      placeholder: param.placeholder ?? this.props.getDefault(param, this.props.parameters.task),
    }));
  }

  renderRegionSection(): JSX.Element {
    return (
      <PrimerWizardSection
        title="Region"
        open={true}
        task={this.props.parameters.task}
        rows={[
          {
            title: 'Target',
            tasks: [
              {task: PCR, advanced: false},
              {task: QPCR_DYES, advanced: true},
              {task: QPCR_PROBE, advanced: true},
              {task: SEQUENCING, advanced: false},
            ],
            key: 'target',
            component: (
              <PrimerRowInterval
                inputs={this.getInputs([PRIMER_WIZARD_PARAMS.TARGET_START, PRIMER_WIZARD_PARAMS.TARGET_END])}
                selection={this.props.selection}
                setFromSelection={this.setFromSelection.bind(
                  this,
                  PRIMER_WIZARD_PARAMS.TARGET_START,
                  PRIMER_WIZARD_PARAMS.TARGET_END
                )}
              />
            ),
          },
          {
            title: 'Included',
            tasks: [
              {task: PCR, advanced: true},
              {task: QPCR_DYES, advanced: false},
              {task: QPCR_PROBE, advanced: false},
              {task: SEQUENCING, advanced: true},
            ],
            key: 'included',
            component: (
              <PrimerRowInterval
                inputs={this.getInputs([
                  PRIMER_WIZARD_PARAMS.INCLUDED_START,
                  PRIMER_WIZARD_PARAMS.INCLUDED_END,
                ])}
                selection={this.props.selection}
                setFromSelection={this.setFromSelection.bind(
                  this,
                  PRIMER_WIZARD_PARAMS.INCLUDED_START,
                  PRIMER_WIZARD_PARAMS.INCLUDED_END
                )}
              />
            ),
          },
          {
            title: 'Excluded',
            key: 'excluded',
            tasks: [
              {task: PCR, advanced: true},
              {task: SEQUENCING, advanced: true},
            ],
            component: (
              <PrimerRowIntervalList
                inputs={this.getInputs([PRIMER_WIZARD_PARAMS.EXCLUDED_REGION])}
                selection={this.props.selection}
              />
            ),
          },
          {
            title: 'Excluded (primers)',
            tasks: [
              {task: QPCR_DYES, advanced: true},
              {task: QPCR_PROBE, advanced: true},
            ],
            key: 'excluded-primers',
            component: (
              <PrimerRowIntervalList
                inputs={this.getInputs([PRIMER_WIZARD_PARAMS.EXCLUDED_REGION])}
                selection={this.props.selection}
              />
            ),
          },
          {
            title: 'Excluded (probe)',
            tasks: [{task: QPCR_PROBE, advanced: true}],
            key: 'excluded_probe',
            component: (
              <PrimerRowIntervalList
                inputs={this.getInputs([PRIMER_WIZARD_PARAMS.EXCLUDED_PROBE_REGION])}
                selection={this.props.selection}
              />
            ),
          },
        ]}
      />
    );
  }

  renderPrimerSection(): JSX.Element {
    return (
      <PrimerWizardSection
        title="Primer"
        open={false}
        task={this.props.parameters.task}
        rows={[
          {
            title: '',
            tasks: [
              {task: PCR, advanced: false},
              {task: QPCR_DYES, advanced: false},
              {task: QPCR_PROBE, advanced: false},
              {task: SEQUENCING, advanced: false},
            ],
            key: 'header',
            component: <PrimerRowHeader inputs={['Min', 'Opt', 'Max']} />,
          },
          {
            title: 'GC%',
            tasks: [
              {task: PCR, advanced: false},
              {task: QPCR_DYES, advanced: false},
              {task: QPCR_PROBE, advanced: false},
              {task: SEQUENCING, advanced: false},
            ],
            key: 'gc',
            component: (
              <PrimerRowOnlyFields
                inputs={this.getInputs([
                  PRIMER_WIZARD_PARAMS.GC_MIN,
                  PRIMER_WIZARD_PARAMS.GC_OPT,
                  PRIMER_WIZARD_PARAMS.GC_MAX,
                ])}
              />
            ),
          },
          {
            title: MELTING_TEMP_ABBREVIATION,
            tasks: [
              {task: PCR, advanced: false},
              {task: QPCR_DYES, advanced: false},
              {task: QPCR_PROBE, advanced: false},
              {task: SEQUENCING, advanced: false},
            ],
            key: 'tm',
            component: (
              <PrimerRowOnlyFields
                inputs={this.getInputs([
                  PRIMER_WIZARD_PARAMS.TM_MIN,
                  PRIMER_WIZARD_PARAMS.TM_OPT,
                  PRIMER_WIZARD_PARAMS.TM_MAX,
                ])}
              />
            ),
          },
          {
            title: 'Size',
            tasks: [
              {task: PCR, advanced: false},
              {task: QPCR_DYES, advanced: false},
              {task: QPCR_PROBE, advanced: false},
              {task: SEQUENCING, advanced: false},
            ],
            key: 'size',
            component: (
              <PrimerRowOnlyFields
                inputs={this.getInputs([
                  PRIMER_WIZARD_PARAMS.SIZE_MIN,
                  PRIMER_WIZARD_PARAMS.SIZE_OPT,
                  PRIMER_WIZARD_PARAMS.SIZE_MAX,
                ])}
              />
            ),
          },
          {
            title: "3' GC clamp",
            tasks: [
              {task: PCR, advanced: false},
              {task: QPCR_DYES, advanced: false},
              {task: QPCR_PROBE, advanced: false},
              {task: SEQUENCING, advanced: false},
            ],
            key: 'gc-clamp',
            component: <PrimerRowOnlyFields inputs={this.getInputs([PRIMER_WIZARD_PARAMS.GC_CLAMP])} />,
          },
        ]}
      />
    );
  }

  renderProbeSection(): JSX.Element {
    return (
      <PrimerWizardSection
        title="Probe"
        open={false}
        task={this.props.parameters.task}
        rows={[
          {
            title: '',
            tasks: [{task: QPCR_PROBE, advanced: false}],
            key: 'header',
            component: <PrimerRowHeader inputs={['Min', 'Opt', 'Max']} />,
          },
          {
            title: 'GC%',
            tasks: [{task: QPCR_PROBE, advanced: false}],
            key: 'gc',
            component: (
              <PrimerRowOnlyFields
                inputs={this.getInputs([
                  PRIMER_WIZARD_PARAMS.GC_PROBE_MIN,
                  PRIMER_WIZARD_PARAMS.GC_PROBE_OPT,
                  PRIMER_WIZARD_PARAMS.GC_PROBE_MAX,
                ])}
              />
            ),
          },
          {
            title: MELTING_TEMP_ABBREVIATION,
            tasks: [{task: QPCR_PROBE, advanced: false}],
            key: 'tm',
            component: (
              <PrimerRowOnlyFields
                inputs={this.getInputs([
                  PRIMER_WIZARD_PARAMS.TM_PROBE_MIN,
                  PRIMER_WIZARD_PARAMS.TM_PROBE_OPT,
                  PRIMER_WIZARD_PARAMS.TM_PROBE_MAX,
                ])}
              />
            ),
          },
          {
            title: 'Size',
            tasks: [{task: QPCR_PROBE, advanced: false}],
            key: 'size',
            component: (
              <PrimerRowOnlyFields
                inputs={this.getInputs([
                  PRIMER_WIZARD_PARAMS.SIZE_PROBE_MIN,
                  PRIMER_WIZARD_PARAMS.SIZE_PROBE_OPT,
                  PRIMER_WIZARD_PARAMS.SIZE_PROBE_MAX,
                ])}
              />
            ),
          },
        ]}
      />
    );
  }

  renderAmpliconSection(): JSX.Element {
    return (
      <PrimerWizardSection
        title="Amplicon"
        open={false}
        task={this.props.parameters.task}
        rows={[
          {
            title: '',
            tasks: [
              {task: PCR, advanced: false},
              {task: QPCR_DYES, advanced: false},
              {task: QPCR_PROBE, advanced: false},
            ],
            key: 'header',
            component: <PrimerRowHeader inputs={['Min', 'Opt', 'Max']} />,
          },
          {
            title: 'Size',
            tasks: [
              {task: PCR, advanced: false},
              {task: QPCR_DYES, advanced: false},
              {task: QPCR_PROBE, advanced: false},
            ],
            key: 'size',
            component: (
              <PrimerRowOnlyFields
                inputs={this.getInputs([
                  PRIMER_WIZARD_PARAMS.PRODUCT_MIN_SIZE,
                  PRIMER_WIZARD_PARAMS.PRODUCT_OPT_SIZE,
                  PRIMER_WIZARD_PARAMS.PRODUCT_MAX_SIZE,
                ])}
              />
            ),
          },
        ]}
      />
    );
  }

  renderResultGeneration(): JSX.Element {
    return (
      <PrimerWizardSection
        title="Result Generation"
        open={false}
        task={this.props.parameters.task}
        rows={[
          {
            title: '# results',
            tasks: [
              {task: PCR, advanced: false},
              {task: QPCR_DYES, advanced: false},
              {task: QPCR_PROBE, advanced: false},
              {task: SEQUENCING, advanced: false},
            ],
            key: 'num-results',
            component: <PrimerRowOnlyFields inputs={this.getInputs([PRIMER_WIZARD_PARAMS.NUM_RESULTS])} />,
          },
        ]}
      />
    );
  }

  renderPartialDesignInput(): JSX.Element {
    return (
      <PrimerWizardSection
        title="Partial Design"
        open={false}
        task={this.props.parameters.task}
        rows={[
          {
            title: 'Forward sequence',
            tasks: [
              {task: PCR, advanced: false},
              {task: QPCR_DYES, advanced: false},
              {task: QPCR_PROBE, advanced: false},
            ],
            key: 'forward-sequence',
            component: (
              <PrimerRowLongField inputs={this.getInputs([PRIMER_WIZARD_PARAMS.FORWARD_PRIMER_SEQUENCE])} />
            ),
          },
          {
            title: 'Reverse sequence',
            tasks: [
              {task: PCR, advanced: false},
              {task: QPCR_DYES, advanced: false},
              {task: QPCR_PROBE, advanced: false},
            ],
            key: 'reverse-sequence',
            component: (
              <PrimerRowLongField inputs={this.getInputs([PRIMER_WIZARD_PARAMS.REVERSE_PRIMER_SEQUENCE])} />
            ),
          },
          {
            title: 'Probe sequence',
            tasks: [{task: QPCR_PROBE, advanced: false}],
            key: 'probe-sequence',
            component: (
              <PrimerRowLongField inputs={this.getInputs([PRIMER_WIZARD_PARAMS.INTERNAL_OLIGO_SEQUENCE])} />
            ),
          },
        ]}
      />
    );
  }

  renderDesignAcrossJunctions(): JSX.Element {
    return (
      <PrimerWizardSection
        title="Design Across Junctions"
        open={false}
        task={this.props.parameters.task}
        rows={[
          {
            title: 'Overlap junctions',
            tasks: [
              {task: PCR, advanced: false},
              {task: QPCR_DYES, advanced: false},
              {task: QPCR_PROBE, advanced: false},
            ],
            key: 'overlap-junctions',
            component: (
              <PrimerRowOnlyFields inputs={this.getInputs([PRIMER_WIZARD_PARAMS.OVERLAP_JXN_LIST])} />
            ),
          },
          {
            title: "Min 3' overlap",
            tasks: [
              {task: PCR, advanced: false},
              {task: QPCR_DYES, advanced: false},
              {task: QPCR_PROBE, advanced: false},
            ],
            key: "min-3'-overlap",
            component: (
              <PrimerRowOnlyFields inputs={this.getInputs([PRIMER_WIZARD_PARAMS.MIN_3_PRIME_OVERLAP])} />
            ),
          },
          {
            title: "Min 5' overlap",
            tasks: [
              {task: PCR, advanced: false},
              {task: QPCR_DYES, advanced: false},
              {task: QPCR_PROBE, advanced: false},
            ],
            key: "min-5'-overlap",
            component: (
              <PrimerRowOnlyFields inputs={this.getInputs([PRIMER_WIZARD_PARAMS.MIN_5_PRIME_OVERLAP])} />
            ),
          },
        ]}
      />
    );
  }

  renderForceLocation(): JSX.Element {
    return (
      <PrimerWizardSection
        title="Force Location"
        open={false}
        task={this.props.parameters.task}
        rows={[
          {
            title: '',
            key: 'header',
            tasks: [
              {task: PCR, advanced: false},
              {task: QPCR_DYES, advanced: false},
              {task: QPCR_PROBE, advanced: false},
            ],
            component: <PrimerRowHeader inputs={["Start (5' end)", "End (3' end)"]} />,
          },
          {
            title: 'Forward',
            tasks: [
              {task: PCR, advanced: false},
              {task: QPCR_DYES, advanced: false},
              {task: QPCR_PROBE, advanced: false},
            ],
            key: 'forward',
            component: (
              <PrimerRowOnlyFields
                inputs={this.getInputs([
                  PRIMER_WIZARD_PARAMS.FORCE_FORWARD_START,
                  PRIMER_WIZARD_PARAMS.FORCE_FORWARD_END,
                ])}
              />
            ),
          },
          {
            title: 'Reverse',
            tasks: [
              {task: PCR, advanced: false},
              {task: QPCR_DYES, advanced: false},
              {task: QPCR_PROBE, advanced: false},
            ],
            key: 'reverse',
            component: (
              <PrimerRowOnlyFields
                inputs={this.getInputs([
                  PRIMER_WIZARD_PARAMS.FORCE_REVERSE_START,
                  PRIMER_WIZARD_PARAMS.FORCE_REVERSE_END,
                ])}
              />
            ),
          },
        ]}
      />
    );
  }

  renderSequencingSection(): JSX.Element {
    return (
      <PrimerWizardSection
        title="Sequencing"
        open={false}
        task={this.props.parameters.task}
        rows={[
          {
            title: 'Spacing',
            tasks: [{task: SEQUENCING, advanced: false}],
            key: 'spacing',
            component: (
              <PrimerRowOnlyFields inputs={this.getInputs([PRIMER_WIZARD_PARAMS.SEQUENCING_SPACING])} />
            ),
          },
          {
            title: 'Interval',
            tasks: [{task: SEQUENCING, advanced: false}],
            key: 'interval',
            component: (
              <PrimerRowOnlyFields inputs={this.getInputs([PRIMER_WIZARD_PARAMS.SEQUENCING_INTERVAL])} />
            ),
          },
          {
            title: 'Lead',
            tasks: [{task: SEQUENCING, advanced: false}],
            key: 'lead',
            component: (
              <PrimerRowOnlyFields inputs={this.getInputs([PRIMER_WIZARD_PARAMS.SEQUENCING_LEAD])} />
            ),
          },
          {
            title: 'Accuracy',
            tasks: [{task: SEQUENCING, advanced: false}],
            key: 'accuracy',
            component: (
              <PrimerRowOnlyFields inputs={this.getInputs([PRIMER_WIZARD_PARAMS.SEQUENCING_ACCURACY])} />
            ),
          },
        ]}
      />
    );
  }

  override render(): JSX.Element {
    return (
      <div className="buffer-content-container">
        <form className="form-horizontal">
          {this.renderHeader()}
          <div className="panel-group u-spacing-top">
            {this.renderRegionSection()}
            {this.renderPrimerSection()}
            {this.renderProbeSection()}
            {this.renderAmpliconSection()}
            {this.renderResultGeneration()}
            {this.renderPartialDesignInput()}
            {this.renderDesignAcrossJunctions()}
            {this.renderForceLocation()}
            {this.renderSequencingSection()}
          </div>
          <div className="u-spacing-top">
            {'Our primer wizard is powered by Primer3. Visit the '}
            <a
              href="https://www.primer3plus.com/primer3plusHelp.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              manual
            </a>
            {
              ' for more information on the different tasks and parameters. Primer3 does not support RNA primers, so only DNA primers may be created with the Primer Wizard tool.'
            }
          </div>
        </form>
      </div>
    );
  }
}

export default PrimerWizard;
